import { Box, Field, NumberInput } from '@chakra-ui/react';
import React, { useCallback } from 'react';

interface CurrencyInputProps {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  onChangeNumber?: (value: number) => void; // Optional callback with numeric value
  onBlur?: () => void;
  onFocus?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  isRequired?: boolean;
  isDisabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  size?: 'sm' | 'md' | 'lg';
  /** Skip auto-select on focus (used during programmatic navigation) */
  skipAutoSelect?: boolean;
}

export const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      label,
      value,
      onChange = () => {},
      onChangeNumber,
      onBlur,
      onFocus,
      onKeyDown,
      isRequired = false,
      isDisabled = false,
      step = 1,
      min,
      max,
      size = 'md',
      skipAutoSelect = false,
    },
    ref
  ) => {
    const internalRef = React.useRef<HTMLInputElement>(null);

    // Forward the internal ref to the parent using useImperativeHandle
    React.useImperativeHandle(ref, () => internalRef.current!, []);

    const handleInputFocus = useCallback(() => {
      // Prevent focus if disabled
      if (isDisabled) {
        internalRef.current?.blur();
        return;
      }

      // Call external onFocus handler first
      onFocus?.();

      // Skip auto-select during programmatic navigation (navigation handles select)
      if (skipAutoSelect) {
        return;
      }

      // Select all text when input receives focus (user-initiated only)
      // Use requestAnimationFrame for better timing than setTimeout
      requestAnimationFrame(() => {
        internalRef.current?.select();
      });
    }, [isDisabled, onFocus, skipAutoSelect]);

    const handleInputBlur = useCallback(() => {
      onBlur?.();
    }, [onBlur]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Call custom onKeyDown handler if provided
        if (onKeyDown) {
          onKeyDown(e);
          return;
        }
        // Default behavior: blur on Enter
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        }
      },
      [onKeyDown]
    );

    const inputElement = (
      <Box width="100%" bg={isDisabled ? 'gray.100' : undefined}>
        <NumberInput.Root
          value={value || ''}
          onValueChange={({ value, valueAsNumber }) => {
            // Prevent changes when disabled
            if (isDisabled) return;
            onChange(value);
            if (onChangeNumber !== undefined && !Number.isNaN(valueAsNumber)) {
              onChangeNumber(valueAsNumber);
            }
          }}
          step={step}
          min={min}
          max={max}
          width="100%"
          size={size}
          disabled={isDisabled}
          formatOptions={{
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
        >
          <NumberInput.Input
            ref={internalRef}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            tabIndex={isDisabled ? -1 : undefined}
            readOnly={isDisabled}
          />
        </NumberInput.Root>
      </Box>
    );

    if (!label) {
      return inputElement;
    }

    return (
      <Field.Root required={isRequired}>
        <Field.Label>
          {label} {isRequired && <Field.RequiredIndicator />}
        </Field.Label>
        {inputElement}
      </Field.Root>
    );
  }
);
